import { SyntheticEvent, useMemo } from 'react';

import { Clear } from '@mui/icons-material';
import { Autocomplete, Box, IconButton, ListItemText, TextField, TextFieldProps } from '@mui/material';

import { usePersistedSetting } from '../hooks/usePersistedSetting';

const maxAttributeKeyHistory = 50;
const storageKey = 'portal.attributeKeyHistory';

type AttributeKeyAutocompleteProps = {
	label?: string;
	value: string;
	onChange: (value: string) => void;
	onCommit?: (value: string) => void;
	sx?: TextFieldProps['sx'];
};

function normalizeKey(value: string): string {
	return value.trim();
}

function sortKeys(keys: string[]): string[] {
	return [...keys].sort((left, right) => left.localeCompare(right));
}

export default function AttributeKeyAutocomplete({ label = 'Attribute', value, onChange, onCommit, sx }: AttributeKeyAutocompleteProps) {
	const [attributeKeys, setAttributeKeys] = usePersistedSetting<string[]>(storageKey, []);
	const options = useMemo(() => sortKeys(Array.from(new Set(attributeKeys.map(normalizeKey).filter(Boolean)))), [attributeKeys]);

	const rememberKey = (nextValue: string) => {
		const normalizedValue = normalizeKey(nextValue);
		if (!normalizedValue) {
			return;
		}

		setAttributeKeys((current) => {
			const remainingKeys = current.filter((key) => key.toLocaleLowerCase() !== normalizedValue.toLocaleLowerCase());
			return [normalizedValue, ...remainingKeys].slice(0, maxAttributeKeyHistory);
		});
		onCommit?.(normalizedValue);
	};

	const removeKey = (key: string) => {
		setAttributeKeys((current) => current.filter((item) => item.toLocaleLowerCase() !== key.toLocaleLowerCase()));
	};

	const handleChange = (_event: SyntheticEvent, nextValue: string | null) => {
		const resolvedValue = nextValue ?? '';
		onChange(resolvedValue);
		rememberKey(resolvedValue);
	};

	const handleInputChange = (_event: SyntheticEvent, nextValue: string) => {
		onChange(nextValue);
	};

	return (
		<Autocomplete<string, false, false, true>
			freeSolo
			options={options}
			value={value}
			inputValue={value}
			onChange={handleChange}
			onInputChange={handleInputChange}
			onBlur={() => rememberKey(value)}
			sx={sx}
			renderOption={(props, option) => (
				<Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<ListItemText primary={option} primaryTypographyProps={{ noWrap: true }} />
					<IconButton
						size="small"
						aria-label={`Remove ${option} from attribute suggestions`}
						onClick={(event) => {
							event.stopPropagation();
							removeKey(option);
						}}
					>
						<Clear fontSize="small" />
					</IconButton>
				</Box>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					size="small"
					label={label}
					onKeyDown={(event) => {
						if (event.key === 'Enter') {
							rememberKey(value);
						}
					}}
				/>
			)}
		/>
	);
}
